/**
 * エンティティ画像サムネイルコンポーネント
 * GCSから画像を取得してサムネイル表示
 * キャッシュと競合対策済み
 */

import React, { useEffect, useState } from "react";
import { getSignedUrl } from "../../utils/imageFetcher";
import "./EntityImageThumbnail.css";

interface EntityImageThumbnailProps {
  // ストレージパス（GCS上のオブジェクト名）
  storagePath?: string;
  // 代替テキスト
  alt?: string;
  // サイズ（幅と高さ）
  size?: number;
  // クリック時のハンドラ
  onClick?: () => void;
  // カスタムクラス名
  className?: string;
  // プレースホルダーアイコン
  placeholderIcon?: string;
}

export const EntityImageThumbnail: React.FC<EntityImageThumbnailProps> = ({
  storagePath,
  alt = "画像",
  size = 48,
  onClick,
  className = "",
  placeholderIcon = "🖼️",
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!storagePath) {
        setImageUrl(null);
        return;
      }

      setIsLoading(true);
      setHasError(false);

      try {
        const url = await getSignedUrl(storagePath);
        if (isMounted) {
          setImageUrl(url);
        }
      } catch (error) {
        console.error("[EntityImageThumbnail] 画像読み込みエラー:", error);
        if (isMounted) {
          setHasError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [storagePath]);

  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
  };

  // ストレージパスがない場合は空のプレースホルダー
  if (!storagePath) {
    return (
      <div
        className={`entity-image-thumbnail placeholder ${className}`}
        style={containerStyle}
        onClick={onClick}
      >
        <span className="placeholder-icon">{placeholderIcon}</span>
      </div>
    );
  }

  // ローディング中
  if (isLoading) {
    return (
      <div
        className={`entity-image-thumbnail loading ${className}`}
        style={containerStyle}
      >
        <div className="loading-spinner" />
      </div>
    );
  }

  // エラー時
  if (hasError || !imageUrl) {
    return (
      <div
        className={`entity-image-thumbnail error ${className}`}
        style={containerStyle}
        onClick={onClick}
      >
        <span className="error-icon">⚠️</span>
      </div>
    );
  }

  return (
    <div
      className={`entity-image-thumbnail ${className}`}
      style={containerStyle}
      onClick={onClick}
    >
      <img
        src={imageUrl}
        alt={alt}
        onError={() => setHasError(true)}
        loading="lazy"
      />
    </div>
  );
};
